import React, { useState } from 'react'
import { Persona, PersonaUpdate, createPersona, PersonaType } from '../api/client'

interface PersonaCreatorProps {
  isOpen: boolean
  onClose: () => void
  onPersonaCreated: (persona: Persona) => void
  isAdmin?: boolean
}

const PersonaCreator: React.FC<PersonaCreatorProps> = ({
  isOpen,
  onClose,
  onPersonaCreated,
  isAdmin = false
}) => {
  const [newPersona, setNewPersona] = useState<PersonaUpdate>({
    first_name: '',
    last_name: null,
    bio: '',
    interaction_style: '',
    nickname: null,
    archetype: null,
    type: null,
    is_public: false
  })
  const [isCreatingPersona, setIsCreatingPersona] = useState(false)
  const [creationError, setCreationError] = useState<string | null>(null)

  const handleCreatePersona = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    const hasArchetype = isAdmin && newPersona.type !== PersonaType.CELEBRITY && newPersona.archetype?.trim()
    const hasFirstName = newPersona.first_name.trim()
    
    if (!newPersona.bio.trim() || !newPersona.interaction_style.trim()) {
      setCreationError('Bio and advising style are required')
      return
    }
    
    if (!hasArchetype && !hasFirstName) {
      setCreationError(isAdmin && newPersona.type !== PersonaType.CELEBRITY ? 'Either first name or archetype is required' : 'First name is required')
      return
    }
    
    setIsCreatingPersona(true)
    setCreationError(null)
    
    try {
      const createdPersona = await createPersona(newPersona)
      
      // Reset form
      setNewPersona({
        first_name: '',
        last_name: null,
        bio: '',
        interaction_style: '',
        nickname: null,
        archetype: null,
        type: null,
        is_public: false
      })
      
      // Notify parent component
      onPersonaCreated(createdPersona)
      
      // Close modal
      onClose()
    } catch (error) {
      console.error('Error creating persona:', error)
      setCreationError('Failed to create persona. Please try again.')
    } finally {
      setIsCreatingPersona(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Create New Advisor</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        {creationError && (
          <div className="mb-4 p-2 bg-rose-100 text-rose-700 rounded-md text-sm">
            {creationError}
          </div>
        )}
        
        <form onSubmit={handleCreatePersona}>
          {isAdmin && (
            <div className="mb-4">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Type*
              </label>
              <select
                id="type"
                value={newPersona.type || ''}
                onChange={(e) => setNewPersona({...newPersona, type: e.target.value as PersonaType || null, archetype: null})}
                className="p-2 border border-gray-300 rounded-md w-full"
                required
              >
                <option value="">Select type...</option>
                <option value={PersonaType.CELEBRITY}>Real Celebrity</option>
                <option value={PersonaType.PERSONAL_RELATION}>Personal Relation</option>
                <option value={PersonaType.ADMIN_CUSTOM}>Fictitious Archetype</option>
              </select>
            </div>
          )}

          {isAdmin && (
            <div className="mb-4">
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-1">
                Visibility*
              </label>
              <select
                id="visibility"
                value={newPersona.is_public ? 'public' : 'private'}
                onChange={(e) => setNewPersona({...newPersona, is_public: e.target.value === 'public'})}
                className="p-2 border border-gray-300 rounded-md w-full"
                required
              >
                <option value="private">Advisor private to you</option>
                <option value="public">Available to all Honeyguide users</option>
              </select>
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name{isAdmin && newPersona.type && newPersona.type !== PersonaType.CELEBRITY ? '' : '*'}
            </label>
            <input
              id="firstName"
              type="text"
              value={newPersona.first_name}
              onChange={(e) => setNewPersona({...newPersona, first_name: e.target.value})}
              className="p-2 border border-gray-300 rounded-md w-full"
              required={!isAdmin || newPersona.type === PersonaType.CELEBRITY || !newPersona.type}
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={newPersona.last_name || ''}
              onChange={(e) => setNewPersona({...newPersona, last_name: e.target.value || null})}
              className="p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
              Nickname
            </label>
            <input
              id="nickname"
              type="text"
              value={newPersona.nickname || ''}
              onChange={(e) => setNewPersona({...newPersona, nickname: e.target.value || null})}
              className="p-2 border border-gray-300 rounded-md w-full"
              placeholder="Optional nickname"
            />
          </div>

          {isAdmin && newPersona.type && newPersona.type !== PersonaType.CELEBRITY && (
            <div className="mb-4">
              <label htmlFor="archetype" className="block text-sm font-medium text-gray-700 mb-1">
                Archetype{!newPersona.first_name?.trim() ? '*' : ''}
              </label>
              <input
                id="archetype"
                type="text"
                value={newPersona.archetype || ''}
                onChange={(e) => setNewPersona({...newPersona, archetype: e.target.value || null})}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="Archetype name (max 25 characters)"
                maxLength={25}
              />
              <p className="text-xs text-gray-500 mt-1">Either first name or archetype is required</p>
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
              Bio*
            </label>
            <textarea
              id="bio"
              value={newPersona.bio}
              onChange={(e) => setNewPersona({...newPersona, bio: e.target.value})}
              rows={3}
              className="p-2 border border-gray-300 rounded-md w-full"
              required
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="interactionStyle" className="block text-sm font-medium text-gray-700 mb-1">
              Advising Style*
            </label>
            <textarea
              id="interactionStyle"
              value={newPersona.interaction_style}
              onChange={(e) => setNewPersona({...newPersona, interaction_style: e.target.value})}
              rows={3}
              className="p-2 border border-gray-300 rounded-md w-full"
              placeholder="Describe how this advisor gives feedback and advice"
              required
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-action text-plum rounded-md hover:bg-action-dark disabled:bg-amber-300"
              disabled={isCreatingPersona}
            >
              {isCreatingPersona ? 'Creating...' : 'Create Advisor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PersonaCreator